import { useState, FormEvent, useMemo, ChangeEvent, useEffect } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { MONTH_DAY_YEAR_HOUR_MINUTE_DATE_FORMAT } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import { ButtonTypes } from "@enums/button-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { SelectOption } from "@idw-types/common";
import {
    ApAssertion,
    Assertion,
    ClaimStatus,
    PolicyDecision
} from "@idw-types/data/reference-data/generic";
import {
    AxnCompany,
    AxnRelyingParty,
    SimpleAxnWorkflow,
    AxnRPServiceWithSOID,
    SimpleAxnRpServiceWorkflow,
    AxnCountry,
    AxnSelectedServiceOfferingSO
} from "@idw-types/data/reference-data/axn";
import { VerificationTransactionsSearchFiltersState } from "@idw-types/state/verification-transactions-state";
import { initialVerificationTransactionsState } from "@redux/reducers/verification-transactions-reducer";
import { getTimeRange, getTimeRangeLabel } from "@utils/date-util";
import {
    mapAssertionsToSelectOptions,
    mapEntitiesWithNameAndIdToSelectOptions,
    mapEntitiesWithValueAndKeyToSelectOptions,
    mapRpServicesWithSOIDToSelectOptionsWithStyle,
    mapSelectOptionsToValues,
    mapWorkflowsToSelectOptionsWithStyle,
    mapCountriesToSelectOptions
} from "@utils/select-option-mapping-util";
import { sortStrings } from "@utils/string-util";
import {
    getAvailableRelyingPartyIdsFromCompanyIds,
    getAvailableRpServicesForRelyingPartyIds,
    getAvailableRpServicesForWorkflowIds,
    getAvailableServiceOfferingIdsFromRPServicesWithSOIDs,
    getFilteredRpServicesWithSOIDs,
    getFilteredSelectedData,
    getFilteredSimpleAxnWorkflows,
    getRpServicesWithSOIDAndStatus,
    getSimpleAxnWorkflowsWithStatus
} from "@utils/search-form/verification-transactions/verification-transactions-search-form-util";
import { SearchFormHeaderContainer } from "@containers/common/search-form-header-container/search-form-header-container";
import { SearchFormFilterRow } from "@containers/common/search-form-filter-row/search-form-filter-row";
import { MultiSelectFormGroup } from "@containers/common/multi-select-form-group/multi-select-form-group";
import { ButtonWithSpinner } from "@containers/common/button-with-spinner/button-with-spinner";
import { TimeRangeSearchFormFilterRow } from "@containers/common/time-range-search-form-filter-row/time-range-search-form-filter-row";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import styles from "./verification-transactions-search-form.less";

type VerificationTransactionsSearchFormProps = {
    requestInProgress: boolean;
    selectedServiceOfferingSOs: Array<AxnSelectedServiceOfferingSO>;
    rpServicesWithSOIDs: Array<AxnRPServiceWithSOID>;
    assertions: Array<Assertion>;
    claimStatuses: Array<ClaimStatus>;
    companies: Array<AxnCompany>;
    relyingParties: Array<AxnRelyingParty>;
    policyDecisions: Array<PolicyDecision>;
    apAssertions: Array<ApAssertion>;
    countries: Array<AxnCountry>;
    simpleAxnRpServiceWorkflows: Array<SimpleAxnRpServiceWorkflow>;
    simpleAxnWorkflows: Array<SimpleAxnWorkflow>;
    searchFilters: VerificationTransactionsSearchFiltersState;
    handleSearch: (searchFilters: VerificationTransactionsSearchFiltersState) => void;
    handleCancelSearch: () => void;
};

type VerificationTransactionsSearchFormState = {
    credentials: string;
    selectedCountryOptions: Array<SelectOption>;
    selectedServiceOfferingOptions: Array<SelectOption>;
    selectedAssertionOptions: Array<SelectOption>;
    selectedClaimStatusOptions: Array<SelectOption>;
    selectedCompanyOptions: Array<SelectOption>;
    selectedRelyingPartyOptions: Array<SelectOption>;
    selectedRpServiceOptions: Array<SelectOption>;
    appId: string;
    transactionId: string;
    selectedPolicyDecisionOptions: Array<SelectOption>;
    sessionKey: string;
    selectedWorkflowOptions: Array<SelectOption>;
    selectedTimeRange: SelectOption;
    startDate: Date;
    endDate: Date;
    includeArchivedServicesAndWorkflows: boolean;
};

export const VerificationTransactionsSearchForm = (props: VerificationTransactionsSearchFormProps) => {
    const maxDate = useMemo(() => new Date(), []);
    const minDate = useMemo(() => new Date(2015, 0, 1), []);
    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const {
        requestInProgress,
        selectedServiceOfferingSOs,
        rpServicesWithSOIDs,
        assertions,
        apAssertions,
        claimStatuses,
        companies,
        relyingParties,
        policyDecisions,
        simpleAxnRpServiceWorkflows,
        simpleAxnWorkflows,
        searchFilters,
        countries,
        handleSearch,
        handleCancelSearch
    } = props;

    const simpleAxnWorkflowsWithStatus: Array<SimpleAxnWorkflow> = useMemo(() => {
        return getSimpleAxnWorkflowsWithStatus(simpleAxnWorkflows);
    }, [simpleAxnWorkflows]);

    const rpServicesWithSOIDAndStatus: Array<AxnRPServiceWithSOID> = useMemo(() => {
        return getRpServicesWithSOIDAndStatus(rpServicesWithSOIDs);
    }, [rpServicesWithSOIDs]);

    const getFilterStateFromSearchFilters = (searchFilters: VerificationTransactionsSearchFiltersState): VerificationTransactionsSearchFormState => {
        let selectedCompanies = companies.filter(company => searchFilters.companyIds.includes(company.id));

        if (companies.length === 1) {
            selectedCompanies = companies;
        }

        const selectedRelyingParties = relyingParties.filter(rp => searchFilters.relyingPartyIds.includes(rp.id));
        const selectedServiceOfferings = selectedServiceOfferingSOs.filter(so => searchFilters.serviceOfferingIds.includes(so.id));
        const selectedAssertions = assertions.filter(assertion => searchFilters.assertionIds.includes(assertion.id));
        const selectedClaimStatuses = claimStatuses.filter(claimStatus => searchFilters.claimStatuses.includes(claimStatus.value));

        const selectedCountries = countries.filter(rp => searchFilters.countries.includes(rp.alpha2));

        const lowercasePolicyDecisions = searchFilters.policyDecisions.map(pd => pd.toLowerCase().replace(" ", ""));
        const selectedPolicyDecisions = policyDecisions.filter(policy => lowercasePolicyDecisions.includes(policy.key.toLowerCase()));

        //if not including archived, filter them out
        let selectedRpServices = rpServicesWithSOIDAndStatus.filter(rpService => searchFilters.rpServiceIds.includes(rpService.id));
        let selectedWorkflows = simpleAxnWorkflowsWithStatus.filter(workflow => searchFilters.workflowIds.includes(workflow.id));
        if (!searchFilters.includeArchivedServicesAndWorkflows) {
            selectedRpServices = selectedRpServices.filter(service => service.status === EntityStatusTypes.ACTIVE);
            selectedWorkflows = selectedWorkflows.filter(workflow => workflow.status === EntityStatusTypes.ACTIVE);
        }
        return {
            credentials: searchFilters.credentials,
            selectedServiceOfferingOptions: mapEntitiesWithNameAndIdToSelectOptions(selectedServiceOfferings),
            selectedAssertionOptions: mapAssertionsToSelectOptions(selectedAssertions),
            selectedClaimStatusOptions: mapEntitiesWithValueAndKeyToSelectOptions(selectedClaimStatuses),
            selectedCompanyOptions: mapEntitiesWithNameAndIdToSelectOptions(selectedCompanies),
            selectedRelyingPartyOptions: mapEntitiesWithNameAndIdToSelectOptions(selectedRelyingParties),
            selectedRpServiceOptions: mapRpServicesWithSOIDToSelectOptionsWithStyle(selectedRpServices),
            appId: searchFilters.appId,
            transactionId: searchFilters.transactionId,
            selectedPolicyDecisionOptions: mapEntitiesWithValueAndKeyToSelectOptions(selectedPolicyDecisions),
            sessionKey: searchFilters.sessionKey,
            selectedWorkflowOptions: mapWorkflowsToSelectOptionsWithStyle(selectedWorkflows),
            selectedTimeRange: {
                label: getTimeRangeLabel(searchFilters.timeRangeType),
                value: searchFilters.timeRangeType
            },
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate,
            includeArchivedServicesAndWorkflows: searchFilters.includeArchivedServicesAndWorkflows,
            selectedCountryOptions: mapCountriesToSelectOptions(selectedCountries)
        };
    };

    const [showFilters, setShowFilters] = useState(true);
    const [filterState, setFilterState] = useState<VerificationTransactionsSearchFormState>(getFilterStateFromSearchFilters(searchFilters));

    const filteredSimpleAxnWorkflows: Array<SimpleAxnWorkflow> = useMemo(() => {
        return getFilteredSimpleAxnWorkflows(filterState.includeArchivedServicesAndWorkflows, simpleAxnWorkflowsWithStatus);
    }, [simpleAxnWorkflowsWithStatus, filterState.includeArchivedServicesAndWorkflows]);

    const filteredRpServicesWithSOIDs: Array<AxnRPServiceWithSOID> = useMemo(() => {
        return getFilteredRpServicesWithSOIDs(filterState.includeArchivedServicesAndWorkflows, rpServicesWithSOIDAndStatus);
    }, [rpServicesWithSOIDAndStatus, filterState.includeArchivedServicesAndWorkflows]);

    useEffect(() => {
        setFilterState(getFilterStateFromSearchFilters(searchFilters));
    }, [searchFilters]);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const serviceOfferingIds = mapSelectOptionsToValues(filterState.selectedServiceOfferingOptions);
        const assertionIds = mapSelectOptionsToValues(filterState.selectedAssertionOptions);
        const claimStatuses = mapSelectOptionsToValues(filterState.selectedClaimStatusOptions);
        const companyIds = mapSelectOptionsToValues(filterState.selectedCompanyOptions);
        const relyingPartyIds = mapSelectOptionsToValues(filterState.selectedRelyingPartyOptions);
        const rpServiceIds = mapSelectOptionsToValues(filterState.selectedRpServiceOptions);
        const policyDecisions = mapSelectOptionsToValues(filterState.selectedPolicyDecisionOptions);
        const workflowIds = mapSelectOptionsToValues(filterState.selectedWorkflowOptions);
        const timeRangeType = filterState.selectedTimeRange.value as TimeRangeTypes;
        let startDate = filterState.startDate;
        let endDate = filterState.endDate;

        const countries = mapSelectOptionsToValues(filterState.selectedCountryOptions);

        const timeRangesUsingCurrentTime = [
            TimeRangeTypes.PAST_MINUTE,
            TimeRangeTypes.PAST_THREE_MINUTES,
            TimeRangeTypes.PAST_FIVE_MINUTES,
            TimeRangeTypes.PAST_TEN_MINUTES,
            TimeRangeTypes.PAST_FIFTEEN_MINUTES,
            TimeRangeTypes.PAST_THIRTY_MINUTES,
            TimeRangeTypes.PAST_ONE_HOUR,
            TimeRangeTypes.PAST_THREE_HOURS,
            TimeRangeTypes.PAST_SIX_HOURS,
            TimeRangeTypes.PAST_TWELVE_HOURS,
            TimeRangeTypes.PAST_TWENTY_FOUR_HOURS
        ];

        if (timeRangesUsingCurrentTime.includes(timeRangeType)) {
            startDate = getTimeRange(timeRangeType).startDate;
            endDate = getTimeRange(timeRangeType).endDate;
        }

        handleSearch({
            credentials: filterState.credentials,
            serviceOfferingIds,
            assertionIds,
            claimStatuses,
            companyIds,
            relyingPartyIds,
            rpServiceIds,
            appId: filterState.appId,
            transactionId: filterState.transactionId,
            policyDecisions,
            sessionKey: filterState.sessionKey,
            workflowIds,
            timeRangeType: filterState.selectedTimeRange.value as TimeRangeTypes,
            startDate: startDate,
            endDate: endDate,
            includeArchivedServicesAndWorkflows: filterState.includeArchivedServicesAndWorkflows,
            countries
        });
    };

    const clearFilterState = () => {
        setFilterState(getFilterStateFromSearchFilters(initialVerificationTransactionsState.searchFilters));
    };

    const toggleShowFilters = () => {
        setShowFilters(!showFilters);
    };

    const handleUpdateCredentials = (event: ChangeEvent<HTMLInputElement>) => {
        setFilterState({
            ...filterState,
            credentials: event.target.value
        });
    };

    const handleUpdateSelectedClaimStatusOptions = (newValue: Array<SelectOption>) => {
        setFilterState({
            ...filterState,
            selectedClaimStatusOptions: newValue
        });
    };

    const handleUpdateSelectedOptions = (
        companyIds: Array<string>,
        relyingPartyIds: Array<string>,
        workflowIds: Array<string>,
        rpServiceIds: Array<string>,
        serviceOfferingIds: Array<string>,
        assertionIds: Array<string>
    ) => {
        const data = getFilteredSelectedData(
            filteredRpServicesWithSOIDs,
            selectedServiceOfferingSOs,
            apAssertions,
            assertions,
            filteredSimpleAxnWorkflows,
            simpleAxnRpServiceWorkflows,
            relyingParties,
            companies,
            companyIds,
            relyingPartyIds,
            workflowIds,
            rpServiceIds,
            serviceOfferingIds,
            assertionIds
        );

        setFilterState({
            ...filterState,
            selectedCompanyOptions: data.filteredSelectedCompanyOptions,
            selectedRelyingPartyOptions: data.filteredSelectedRelyingPartyOptions,
            selectedWorkflowOptions: data.filteredSelectedWorkflowOptions,
            selectedRpServiceOptions: data.filteredSelectedRpServiceOptions,
            selectedServiceOfferingOptions: data.filteredSelectedServiceOfferingOptions,
            selectedAssertionOptions: data.filteredSelectedAssertionOptions
        });
    }

    const handleUpdateSelectedCompanyOptions = (newValue: Array<SelectOption>) => {
        const selectedCompanyIds = newValue.map(option => option.value);
        const selectedRelyingPartyIds = filterState.selectedRelyingPartyOptions.map(option => option.value);
        const selectedWorkflowIds = filterState.selectedWorkflowOptions.map(option => option.value);
        const selectedRpServiceIds = filterState.selectedRpServiceOptions.map(option => option.value);
        const selectedServiceOfferingIds = filterState.selectedServiceOfferingOptions.map(option => option.value);
        const selectedAssertionIds = filterState.selectedAssertionOptions.map(option => option.value);

        handleUpdateSelectedOptions(selectedCompanyIds, selectedRelyingPartyIds, selectedWorkflowIds, selectedRpServiceIds, selectedServiceOfferingIds, selectedAssertionIds);
    };

    const handleUpdateSelectedRelyingPartyOptions = (newValue: Array<SelectOption>) => {
        const selectedCompanyIds = filterState.selectedCompanyOptions.map(option => option.value);
        const selectedRelyingPartyIds = newValue.map(option => option.value);
        const selectedWorkflowIds = filterState.selectedWorkflowOptions.map(option => option.value);
        const selectedRpServiceIds = filterState.selectedRpServiceOptions.map(option => option.value);
        const selectedServiceOfferingIds = filterState.selectedServiceOfferingOptions.map(option => option.value);
        const selectedAssertionIds = filterState.selectedAssertionOptions.map(option => option.value);

        handleUpdateSelectedOptions(selectedCompanyIds, selectedRelyingPartyIds, selectedWorkflowIds, selectedRpServiceIds, selectedServiceOfferingIds, selectedAssertionIds);
    };

    const handleUpdateSelectedWorkflowOptions = (newValue: Array<SelectOption>) => {
        const selectedCompanyIds = filterState.selectedCompanyOptions.map(option => option.value);
        const selectedRelyingPartyIds = filterState.selectedRelyingPartyOptions.map(option => option.value);
        const selectedWorkflowIds = newValue.map(option => option.value);
        const selectedRpServiceIds = filterState.selectedRpServiceOptions.map(option => option.value);
        const selectedServiceOfferingIds = filterState.selectedServiceOfferingOptions.map(option => option.value);
        const selectedAssertionIds = filterState.selectedAssertionOptions.map(option => option.value);

        handleUpdateSelectedOptions(selectedCompanyIds, selectedRelyingPartyIds, selectedWorkflowIds, selectedRpServiceIds, selectedServiceOfferingIds, selectedAssertionIds);
    };

    const handleUpdateSelectedRpServiceOptions = (newValue: Array<SelectOption>) => {
        const selectedCompanyIds = filterState.selectedCompanyOptions.map(option => option.value);
        const selectedRelyingPartyIds = filterState.selectedRelyingPartyOptions.map(option => option.value);
        const selectedWorkflowIds = filterState.selectedWorkflowOptions.map(option => option.value);
        const selectedRpServiceIds = newValue.map(option => option.value);
        const selectedServiceOfferingIds = filterState.selectedServiceOfferingOptions.map(option => option.value);
        const selectedAssertionIds = filterState.selectedAssertionOptions.map(option => option.value);

        handleUpdateSelectedOptions(selectedCompanyIds, selectedRelyingPartyIds, selectedWorkflowIds, selectedRpServiceIds, selectedServiceOfferingIds, selectedAssertionIds);
    };

    const handleUpdateSelectedServiceOfferingOptions = (newValue: Array<SelectOption>) => {
        const selectedCompanyIds = filterState.selectedCompanyOptions.map(option => option.value);
        const selectedRelyingPartyIds = filterState.selectedRelyingPartyOptions.map(option => option.value);
        const selectedWorkflowIds = filterState.selectedWorkflowOptions.map(option => option.value);
        const selectedRpServiceIds = filterState.selectedRpServiceOptions.map(option => option.value);
        const selectedServiceOfferingIds = newValue.map(option => option.value);
        const selectedAssertionIds = filterState.selectedAssertionOptions.map(option => option.value);

        handleUpdateSelectedOptions(selectedCompanyIds, selectedRelyingPartyIds, selectedWorkflowIds, selectedRpServiceIds, selectedServiceOfferingIds, selectedAssertionIds);
    };

    const handleUpdateSelectedAssertionOptions = (newValue: Array<SelectOption>) => {
        const selectedCompanyIds = filterState.selectedCompanyOptions.map(option => option.value);
        const selectedRelyingPartyIds = filterState.selectedRelyingPartyOptions.map(option => option.value);
        const selectedWorkflowIds = filterState.selectedWorkflowOptions.map(option => option.value);
        const selectedRpServiceIds = filterState.selectedRpServiceOptions.map(option => option.value);
        const selectedServiceOfferingIds = filterState.selectedServiceOfferingOptions.map(option => option.value);
        const selectedAssertionIds = newValue.map(option => option.value);

        handleUpdateSelectedOptions(selectedCompanyIds, selectedRelyingPartyIds, selectedWorkflowIds, selectedRpServiceIds, selectedServiceOfferingIds, selectedAssertionIds);
    };

    const handleUpdateAppId = (event: ChangeEvent<HTMLInputElement>) => {
        setFilterState({
            ...filterState,
            appId: event.target.value
        });
    };

    const handleUpdateTransactionId = (event: ChangeEvent<HTMLInputElement>) => {
        setFilterState({
            ...filterState,
            transactionId: event.target.value
        });
    };

    const handleUpdateSelectedPolicyDecisionOptions = (newValue: Array<SelectOption>) => {
        setFilterState({
            ...filterState,
            selectedPolicyDecisionOptions: newValue
        });
    };

    const handleUpdateSelectedCountryOptions = (newValue: Array<SelectOption>) => {
        setFilterState({
            ...filterState,
            selectedCountryOptions: newValue
        });
    };

    const handleUpdateSessionKey = (event: ChangeEvent<HTMLInputElement>) => {
        setFilterState({
            ...filterState,
            sessionKey: event.target.value
        });
    };

    const handleTimeRangeChange = (newValue: SelectOption) => {
        if (newValue.value === TimeRangeTypes.CUSTOM) {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue
            });
        } else {
            setFilterState({
                ...filterState,
                selectedTimeRange: newValue,
                ...getTimeRange(newValue.value as TimeRangeTypes)
            });
        }
    };

    const handleStartDateChange = (newStartDate: Date) => {
        setFilterState({
            ...filterState,
            startDate: newStartDate
        });
    };

    const handleEndDateChange = (newEndDate: Date) => {
        setFilterState({
            ...filterState,
            endDate: newEndDate
        });
    };

    const handleIncludeArchivedServicesAndWorkflowsChange = () => {
        let selectedWorkflowOptions = filterState.selectedWorkflowOptions;
        let selectedRpServiceOptions = filterState.selectedRpServiceOptions;
        //if state was include, remove any selected workflows that were inactive from selected list
        if (filterState.includeArchivedServicesAndWorkflows) {
            if (filterState.selectedWorkflowOptions.length > 0) {
                const selectedWorkflowIds = filterState.selectedWorkflowOptions.map(option => option.value);
                const selectedWorkflows = simpleAxnWorkflowsWithStatus.filter(workflow => selectedWorkflowIds.includes(workflow.id) && workflow.status === EntityStatusTypes.ACTIVE);
                selectedWorkflowOptions = mapWorkflowsToSelectOptionsWithStyle(selectedWorkflows);
            }
            if (filterState.selectedRpServiceOptions.length > 0) {
                const selectedRpServiceIds = filterState.selectedRpServiceOptions.map(option => option.value);
                const selectedRpServices = rpServicesWithSOIDAndStatus.filter(service => selectedRpServiceIds.includes(service.id) && service.status === EntityStatusTypes.ACTIVE);
                selectedRpServiceOptions = mapRpServicesWithSOIDToSelectOptionsWithStyle(selectedRpServices);
            }
        }
        //todo - might need to re-filter the service offerings and assertions here

        setFilterState({
            ...filterState,
            selectedWorkflowOptions: selectedWorkflowOptions,
            selectedRpServiceOptions: selectedRpServiceOptions,
            includeArchivedServicesAndWorkflows: !filterState.includeArchivedServicesAndWorkflows
        });
    };

    const timeRangeTypes = [
        TimeRangeTypes.PAST_MINUTE,
        TimeRangeTypes.PAST_THREE_MINUTES,
        TimeRangeTypes.PAST_FIVE_MINUTES,
        TimeRangeTypes.PAST_TEN_MINUTES,
        TimeRangeTypes.PAST_FIFTEEN_MINUTES,
        TimeRangeTypes.PAST_THIRTY_MINUTES,
        TimeRangeTypes.PAST_ONE_HOUR,
        TimeRangeTypes.PAST_THREE_HOURS,
        TimeRangeTypes.PAST_SIX_HOURS,
        TimeRangeTypes.PAST_TWELVE_HOURS,
        TimeRangeTypes.PAST_TWENTY_FOUR_HOURS,
        TimeRangeTypes.TODAY,
        TimeRangeTypes.PAST_SEVEN_DAYS,
        TimeRangeTypes.CUSTOM
    ];

    const companyOptions: Array<SelectOption> = useMemo(() => {
        return mapEntitiesWithNameAndIdToSelectOptions(companies).sort((a, b) => sortStrings(a.label, b.label));
    }, [companies]);

    const relyingPartyOptions: Array<SelectOption> = useMemo(() => {
        if (filterState.selectedCompanyOptions.length > 0) {
            const selectedCompanyIds = filterState.selectedCompanyOptions.map(option => option.value);
            const availableRelyingPartyIds = getAvailableRelyingPartyIdsFromCompanyIds(relyingParties, selectedCompanyIds);
            const availableRelyingParties = relyingParties.filter(rp => availableRelyingPartyIds.includes(rp.id));
            return mapEntitiesWithNameAndIdToSelectOptions(availableRelyingParties).sort((a, b) => sortStrings(a.label, b.label));
        } else {
            return mapEntitiesWithNameAndIdToSelectOptions(relyingParties).sort((a, b) => sortStrings(a.label, b.label));
        }
    }, [
        filterState.selectedCompanyOptions,
        relyingParties
    ]);

    const workflowOptions: Array<SelectOption> = useMemo(() => {
        if (filterState.selectedRelyingPartyOptions.length > 0) {
            const selectedRelyingPartyIds = filterState.selectedRelyingPartyOptions.map(option => option.value);
            const availableWorkflows = filteredSimpleAxnWorkflows.filter(workflow => selectedRelyingPartyIds.includes(workflow.relyingPartyID));
            return mapWorkflowsToSelectOptionsWithStyle(availableWorkflows).sort((a, b) => sortStrings(a.label, b.label));
        } else if (filterState.selectedCompanyOptions.length > 0) {
            const availableCompanyIds = filterState.selectedCompanyOptions.map(option => option.value);
            const availableWorkflows = filteredSimpleAxnWorkflows.filter(workflow => availableCompanyIds.includes(workflow.companyID));
            return mapWorkflowsToSelectOptionsWithStyle(availableWorkflows).sort((a, b) => sortStrings(a.label, b.label));
        } else {
            return mapWorkflowsToSelectOptionsWithStyle(filteredSimpleAxnWorkflows).sort((a, b) => sortStrings(a.label, b.label));
        }
    }, [
        filterState.selectedRelyingPartyOptions,
        filterState.selectedCompanyOptions,
        filteredSimpleAxnWorkflows
    ]);

    const rpServiceOptions: Array<SelectOption> = useMemo(() => {
        if (filterState.selectedWorkflowOptions.length > 0) {
            const selectedWorkflowIds = filterState.selectedWorkflowOptions.map(option => option.value);
            const rpServicesWithWorkflowIds = filteredRpServicesWithSOIDs.map(service => ({
                ...service,
                workflowId: simpleAxnRpServiceWorkflows.find(workflowService => workflowService.rpAXNServiceID === service.id)?.workflowID
            }));
            const availableServices = rpServicesWithWorkflowIds.filter(service => selectedWorkflowIds.includes(service.workflowId));
            return mapRpServicesWithSOIDToSelectOptionsWithStyle(availableServices).sort((a, b) => sortStrings(a.label, b.label));
        } else if (filterState.selectedRelyingPartyOptions.length > 0) {
            const selectedRelyingPartyIds = filterState.selectedRelyingPartyOptions.map(option => option.value);
            const availableServices = filteredRpServicesWithSOIDs.filter(service => selectedRelyingPartyIds.includes(`${service.relyingPartyID}`));
            return mapRpServicesWithSOIDToSelectOptionsWithStyle(availableServices).sort((a, b) => sortStrings(a.label, b.label));
        } else if (filterState.selectedCompanyOptions.length > 0) {
            const selectedCompanyIds = filterState.selectedCompanyOptions.map(option => option.value);
            const availableRelyingPartyIds = getAvailableRelyingPartyIdsFromCompanyIds(relyingParties, selectedCompanyIds);
            const availableServices = filteredRpServicesWithSOIDs.filter(service => availableRelyingPartyIds.includes(`${service.relyingPartyID}`));
            return mapRpServicesWithSOIDToSelectOptionsWithStyle(availableServices).sort((a, b) => sortStrings(a.label, b.label));
        } else {
            return mapRpServicesWithSOIDToSelectOptionsWithStyle(filteredRpServicesWithSOIDs).sort((a, b) => sortStrings(a.label, b.label));
        }
    }, [
        filterState.selectedCompanyOptions,
        filterState.selectedRelyingPartyOptions,
        filterState.selectedWorkflowOptions,
        filteredRpServicesWithSOIDs
    ]);

    const serviceOfferingOptionsHelper = (availableRPsWithSOIDs: Array<AxnRPServiceWithSOID>) => {
        const availableServiceOfferingIds = getAvailableServiceOfferingIdsFromRPServicesWithSOIDs(availableRPsWithSOIDs)
        const availableServiceOfferings = selectedServiceOfferingSOs.filter(so => availableServiceOfferingIds.includes(so.id));
        return mapEntitiesWithNameAndIdToSelectOptions(availableServiceOfferings).sort((a, b) => sortStrings(a.label, b.label));
    };

    const serviceOfferingOptions: Array<SelectOption> = useMemo(() => {
        if (filterState.selectedRpServiceOptions.length > 0) {
            const selectedRpServiceIds = filterState.selectedRpServiceOptions.map(rp => rp.value);
            const availableRPsWithSOIDs = rpServicesWithSOIDs.filter(rpWSOID => selectedRpServiceIds.includes(rpWSOID.id));
            return serviceOfferingOptionsHelper(availableRPsWithSOIDs);
        } else if (filterState.selectedWorkflowOptions.length > 0) {
            const selectedWorkflowIds = filterState.selectedWorkflowOptions.map(option => option.value);
            const availableRPsWithSOIDs = getAvailableRpServicesForWorkflowIds(simpleAxnRpServiceWorkflows, filteredRpServicesWithSOIDs, selectedWorkflowIds)
            return serviceOfferingOptionsHelper(availableRPsWithSOIDs);
        } else if (filterState.selectedRelyingPartyOptions.length > 0) {
            const selectedRelyingPartyIds = filterState.selectedRelyingPartyOptions.map(rp => rp.value);
            const availableRPsWithSOIDs = getAvailableRpServicesForRelyingPartyIds(filteredRpServicesWithSOIDs, selectedRelyingPartyIds);
            return serviceOfferingOptionsHelper(availableRPsWithSOIDs);
        } else if (filterState.selectedCompanyOptions.length > 0) {
            const selectedCompanyIds = filterState.selectedCompanyOptions.map(company => company.value);
            const selectedRelyingPartyIds = getAvailableRelyingPartyIdsFromCompanyIds(relyingParties, selectedCompanyIds);
            const availableRPsWithSOIDs = getAvailableRpServicesForRelyingPartyIds(filteredRpServicesWithSOIDs, selectedRelyingPartyIds);
            return serviceOfferingOptionsHelper(availableRPsWithSOIDs);
        } else {
            return mapEntitiesWithNameAndIdToSelectOptions(selectedServiceOfferingSOs).sort((a, b) => sortStrings(a.label, b.label));
        }
    }, [
        filterState.selectedCompanyOptions,
        filterState.selectedRelyingPartyOptions,
        filterState.selectedRpServiceOptions,
        filterState.selectedWorkflowOptions,
        rpServicesWithSOIDs,
        selectedServiceOfferingSOs,
        simpleAxnRpServiceWorkflows
    ]);

    const assertionOptionsHelper = (availableRPsWithSOIDs: Array<AxnRPServiceWithSOID>) => {
        const availableServiceOfferingIds = getAvailableServiceOfferingIdsFromRPServicesWithSOIDs(availableRPsWithSOIDs)
        const availableApAssertionIds = apAssertions.filter(assertion => availableServiceOfferingIds.includes(assertion.serviceOfferingID)).map(apAssertion => apAssertion.assertionID);
        const availableAssertions = assertions.filter(assertion => availableApAssertionIds.includes(assertion.id));
        return mapAssertionsToSelectOptions(availableAssertions).sort((a, b) => sortStrings(a.label, b.label));
    };

    const assertionOptions: Array<SelectOption> = useMemo(() => {
        if (filterState.selectedServiceOfferingOptions.length > 0) {
            const selectedServiceOfferingIds = filterState.selectedServiceOfferingOptions.map(so => so.value);
            const availableApAssertionIds = apAssertions.filter(assertion => selectedServiceOfferingIds.includes(assertion.serviceOfferingID)).map(apAssertion => apAssertion.assertionID);
            const availableAssertions = assertions.filter(assertion => availableApAssertionIds.includes(assertion.id));
            return mapAssertionsToSelectOptions(availableAssertions).sort((a, b) => sortStrings(a.label, b.label));
        } else if (filterState.selectedRpServiceOptions.length > 0) {
            const selectedRpServiceIds = filterState.selectedRpServiceOptions.map(rp => rp.value);
            const availableRPsWithSOIDs = rpServicesWithSOIDs.filter(rpWSOID => selectedRpServiceIds.includes(rpWSOID.id));
            return assertionOptionsHelper(availableRPsWithSOIDs);
        } else if (filterState.selectedWorkflowOptions.length > 0) {
            const selectedWorkflowIds = filterState.selectedWorkflowOptions.map(option => option.value);
            const availableRPsWithSOIDs = getAvailableRpServicesForWorkflowIds(simpleAxnRpServiceWorkflows, filteredRpServicesWithSOIDs, selectedWorkflowIds)
            return assertionOptionsHelper(availableRPsWithSOIDs);
        } else if (filterState.selectedRelyingPartyOptions.length > 0) {
            const selectedRelyingPartyIds = filterState.selectedRelyingPartyOptions.map(rp => rp.value);
            const availableRPsWithSOIDs = getAvailableRpServicesForRelyingPartyIds(filteredRpServicesWithSOIDs, selectedRelyingPartyIds);
            return assertionOptionsHelper(availableRPsWithSOIDs);
        } else if (filterState.selectedCompanyOptions.length > 0) {
            const selectedCompanyIds = filterState.selectedCompanyOptions.map(company => company.value);
            const selectedRelyingPartyIds = getAvailableRelyingPartyIdsFromCompanyIds(relyingParties, selectedCompanyIds);
            const availableRPsWithSOIDs = getAvailableRpServicesForRelyingPartyIds(filteredRpServicesWithSOIDs, selectedRelyingPartyIds);
            return assertionOptionsHelper(availableRPsWithSOIDs);
        } else {
            return mapAssertionsToSelectOptions(assertions).sort((a, b) => sortStrings(a.label, b.label));
        }
    }, [
        filterState.selectedCompanyOptions,
        filterState.selectedRelyingPartyOptions,
        filterState.selectedRpServiceOptions,
        filterState.selectedServiceOfferingOptions,
        filterState.selectedWorkflowOptions,
        assertions,
        apAssertions,
        rpServicesWithSOIDs,
        simpleAxnRpServiceWorkflows
    ]);

    const claimStatusOptions: Array<SelectOption> = useMemo(() => {
        return mapEntitiesWithValueAndKeyToSelectOptions(claimStatuses);
    }, [claimStatuses]);

    const policyDecisionOptions: Array<SelectOption> = useMemo(() => {
        return mapEntitiesWithValueAndKeyToSelectOptions(policyDecisions);
    }, [policyDecisions]);

    const countryOptions: Array<SelectOption> = useMemo(() => {
        return mapCountriesToSelectOptions(countries);
    }, [countries]);

    const countryDropdown = useMemo(() => {
        return <MultiSelectFormGroup
            automationId={`verification-transactions-search-filters-countries-select`}
            controlId={`verification-transactions-search-filters-countries-select`}
            label={resources.common.searchForm.filters.countries.htmlLabel}
            ariaLabel={resources.common.searchForm.filters.countries.htmlLabel}
            onChangeMulti={handleUpdateSelectedCountryOptions}
            options={countryOptions}
            selectedOptions={filterState.selectedCountryOptions}
            disabled={false}
            isRequired={false}
            isSearchable={true}
            isClearable={true}
            labelWidth={labelWidth}
            layout={layout}
            keepMenuOpenOnSelect={true}
        />
    }, [filterState.selectedCountryOptions]);

    return (
        <Form className={styles.verificationTransactionsSearchForm} onSubmit={handleSubmit}>
            <SearchFormHeaderContainer
                idPrefix={"verification-transactions"}
                showFilters={showFilters}
                showToggle={true}
                toggleShowFilters={toggleShowFilters}
            />
            {showFilters && (
                <>
                    <SearchFormFilterRow>
                        <MultiSelectFormGroup
                            automationId={`verification-transactions-search-filters-organizations-select`}
                            controlId={`verification-transactions-search-filters-organizations-select`}
                            label={resources.common.searchForm.filters.organizations.label}
                            ariaLabel={resources.common.searchForm.filters.organizations.label}
                            onChangeMulti={handleUpdateSelectedCompanyOptions}
                            options={companyOptions}
                            selectedOptions={filterState.selectedCompanyOptions}
                            disabled={companies.length === 1}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={companies.length > 1}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                        <MultiSelectFormGroup
                            automationId={`verification-transactions-search-filters-relying-parties-select`}
                            controlId={`verification-transactions-search-filters-relying-parties-select`}
                            label={resources.common.searchForm.filters.relyingParties.label}
                            ariaLabel={resources.common.searchForm.filters.relyingParties.label}
                            onChangeMulti={handleUpdateSelectedRelyingPartyOptions}
                            options={relyingPartyOptions}
                            selectedOptions={filterState.selectedRelyingPartyOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                        <MultiSelectFormGroup
                            automationId={`verification-transactions-search-filters-workflows-select`}
                            controlId={`verification-transactions-search-filters-workflows-select`}
                            label={resources.common.searchForm.filters.workflows.label}
                            ariaLabel={resources.common.searchForm.filters.workflows.label}
                            onChangeMulti={handleUpdateSelectedWorkflowOptions}
                            options={workflowOptions}
                            selectedOptions={filterState.selectedWorkflowOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                        <MultiSelectFormGroup
                            automationId={`verification-transactions-search-filters-rp-services-select`}
                            controlId={`verification-transactions-search-filters-rp-services-select`}
                            label={resources.common.searchForm.filters.verificationServices.label}
                            ariaLabel={resources.common.searchForm.filters.verificationServices.label}
                            onChangeMulti={handleUpdateSelectedRpServiceOptions}
                            options={rpServiceOptions}
                            selectedOptions={filterState.selectedRpServiceOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                    </SearchFormFilterRow>
                    <SearchFormFilterRow>
                        <MultiSelectFormGroup
                            automationId={`verification-transactions-search-filters-service-offerings-select`}
                            controlId={`verification-transactions-search-filters-service-offerings-select`}
                            label={resources.common.searchForm.filters.serviceOfferings.label}
                            ariaLabel={resources.common.searchForm.filters.serviceOfferings.label}
                            onChangeMulti={handleUpdateSelectedServiceOfferingOptions}
                            options={serviceOfferingOptions}
                            selectedOptions={filterState.selectedServiceOfferingOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                        <MultiSelectFormGroup
                            automationId={`verification-transactions-search-filters-assertions-select`}
                            controlId={`verification-transactions-search-filters-assertions-select`}
                            label={resources.pages.verificationTransactions.searchForm.filters.assertions.label}
                            ariaLabel={resources.pages.verificationTransactions.searchForm.filters.assertions.label}
                            onChangeMulti={handleUpdateSelectedAssertionOptions}
                            options={assertionOptions}
                            selectedOptions={filterState.selectedAssertionOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                        <MultiSelectFormGroup
                            automationId={`verification-transactions-search-filters-claim-status-select`}
                            controlId={`verification-transactions-search-filters-claim-status-select`}
                            label={resources.pages.verificationTransactions.searchForm.filters.claimStatus.label}
                            ariaLabel={resources.pages.verificationTransactions.searchForm.filters.claimStatus.label}
                            onChangeMulti={handleUpdateSelectedClaimStatusOptions}
                            options={claimStatusOptions}
                            selectedOptions={filterState.selectedClaimStatusOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                        <MultiSelectFormGroup
                            automationId={`verification-transactions-search-filters-policy-decisions-select`}
                            controlId={`verification-transactions-search-filters-policy-decisions-select`}
                            label={resources.common.searchForm.filters.policyDecisions.label}
                            ariaLabel={resources.common.searchForm.filters.policyDecisions.label}
                            onChangeMulti={handleUpdateSelectedPolicyDecisionOptions}
                            options={policyDecisionOptions}
                            selectedOptions={filterState.selectedPolicyDecisionOptions}
                            disabled={false}
                            isRequired={false}
                            isSearchable={true}
                            isClearable={true}
                            labelWidth={labelWidth}
                            layout={layout}
                            keepMenuOpenOnSelect={true}
                        />
                    </SearchFormFilterRow>
                    <SearchFormFilterRow>
                        <InputFormGroup
                            controlId={"verification-transactions-search-filters-transaction-id-input"}
                            automationId={"verification-transactions-search-filters-transaction-id-input"}
                            label={resources.pages.verificationTransactions.searchForm.filters.transactionId.label}
                            ariaLabel={resources.pages.verificationTransactions.searchForm.filters.transactionId.label}
                            value={filterState.transactionId}
                            disabled={false}
                            isRequired={false}
                            labelWidth={labelWidth}
                            layout={layout}
                            onChange={handleUpdateTransactionId}
                        />
                        <InputFormGroup
                            controlId={"verification-transactions-search-filters-credential-input"}
                            automationId={"verification-transactions-search-filters-credential-input"}
                            label={resources.pages.verificationTransactions.searchForm.filters.userCredential.label}
                            ariaLabel={resources.pages.verificationTransactions.searchForm.filters.userCredential.label}
                            value={filterState.credentials}
                            disabled={false}
                            isRequired={false}
                            labelWidth={labelWidth}
                            layout={layout}
                            onChange={handleUpdateCredentials}
                        />
                        <InputFormGroup
                            controlId={"verification-transactions-search-filters-app-id-input"}
                            automationId={"verification-transactions-search-filters-app-id-input"}
                            label={resources.pages.verificationTransactions.searchForm.filters.appId.label}
                            ariaLabel={resources.pages.verificationTransactions.searchForm.filters.appId.label}
                            value={filterState.appId}
                            disabled={false}
                            isRequired={false}
                            labelWidth={labelWidth}
                            layout={layout}
                            onChange={handleUpdateAppId}
                        />
                        <InputFormGroup
                            controlId={"verification-transactions-search-filters-session-key-input"}
                            automationId={"verification-transactions-search-filters-session-key-input"}
                            label={resources.pages.verificationTransactions.searchForm.filters.sessionKey.label}
                            ariaLabel={resources.pages.verificationTransactions.searchForm.filters.sessionKey.label}
                            value={filterState.sessionKey}
                            disabled={false}
                            isRequired={false}
                            labelWidth={labelWidth}
                            layout={layout}
                            onChange={handleUpdateSessionKey}
                        />
                    </SearchFormFilterRow>




                    <TimeRangeSearchFormFilterRow
                        leftComponent={countryDropdown}
                        idPrefix={"verification-transactions-search-filters"}
                        labelWidth={labelWidth}
                        layout={layout}
                        onChangeTimeRange={handleTimeRangeChange}
                        onChangeStartDate={handleStartDateChange}
                        onChangeEndDate={handleEndDateChange}
                        selectedTimeRange={filterState.selectedTimeRange}
                        timeRangeTypes={timeRangeTypes}
                        startDateLabel={resources.common.searchForm.filters.startDate.label}
                        startDateAriaLabel={resources.common.searchForm.filters.startDate.label}
                        endDateLabel={resources.common.searchForm.filters.endDate.label}
                        endDateAriaLabel={resources.common.searchForm.filters.endDate.label}
                        minDate={minDate}
                        maxDate={maxDate}
                        dateFormat={MONTH_DAY_YEAR_HOUR_MINUTE_DATE_FORMAT}
                        startDate={filterState.startDate}
                        endDate={filterState.endDate}
                        showMonthDropdown={false}
                        showYearDropdown={false}
                        showMonthYearPicker={false}
                        showTimeSelect={true}
                    />
                    <SearchFormFilterRow>
                        <ToggleFormGroup
                            controlId={"verification-transactions-search-filters-include-archived-services-and-workflows-toggle"}
                            automationId={"verification-transactions-search-filters-include-archived-services-and-workflows-toggle"}
                            className={styles.includeArchivedToggleFormGroup}
                            label={resources.pages.verificationTransactions.searchForm.filters.includeArchivedServicesAndWorkflows.label}
                            ariaLabel={resources.pages.verificationTransactions.searchForm.filters.includeArchivedServicesAndWorkflows.label}
                            value={`${filterState.includeArchivedServicesAndWorkflows}`}
                            disabled={false}
                            isRequired={false}
                            labelWidth={labelWidth}
                            onChange={handleIncludeArchivedServicesAndWorkflowsChange}
                            checked={filterState.includeArchivedServicesAndWorkflows}
                            checkedIcon={false}
                            uncheckedIcon={false}
                        />
                    </SearchFormFilterRow>
                    <div className={styles.buttonDiv}>
                        <Button id="verification-transaction-clear-button" variant={"outline-secondary"} onClick={clearFilterState}>{resources.common.buttons.clear.label}</Button>
                        <Button id="verification-transaction-cancel-button" variant={"outline-secondary"} disabled={!requestInProgress} onClick={handleCancelSearch}>{resources.common.buttons.cancel.label}</Button>
                        <ButtonWithSpinner
                            id="verification-transaction-submit-button"
                            disabled={requestInProgress}
                            hideSpinner={false}
                            variant="primary"
                            type={ButtonTypes.SUBMIT}
                            label={resources.common.buttons.search.label}
                        />
                    </div>
                </>
            )}
        </Form >
    );
};